import React, { useEffect, useMemo, useState } from 'react';
import DataTable from '../../components/DataTablePtBr';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import CurrencyInput from '../../components/CurrencyInput';
import api from '../../services/api';
import { showConfirmation, showError, showSuccess } from '../../services/alert';

const initialForm = {
  safraId: '',
  talhaoId: '',
  sementeInsumoId: '',
  cultura: '',
  variedade: '',
  dataPlantio: new Date().toISOString().slice(0, 10),
  areaPlantada: '',
  quantidadeSementeUsada: '',
  unidade: 'KG',
  custoEstimado: '',
  status: 2,
  observacao: ''
};

const statusLabels = {
  1: 'Planejado',
  2: 'Plantado',
  3: 'Em andamento',
  4: 'Colhido',
  5: 'Cancelado'
};

function PlantioList() {
  const navigate = useNavigate();
  const [plantios, setPlantios] = useState([]);
  const [safras, setSafras] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const safraSelecionada = useMemo(
    () => safras.find((item) => String(item.id) === String(form.safraId)),
    [safras, form.safraId]
  );

  const talhaoDaSafra = useMemo(() => {
    if (!safraSelecionada) return null;
    return talhoes.find((item) => String(item.id) === String(safraSelecionada.talhaoId));
  }, [safraSelecionada, talhoes]);

  const carregar = async () => {
    setLoading(true);
    try {
      const [plantiosRes, safrasRes, talhoesRes, insumosRes] = await Promise.all([
        api.get('/plantios'),
        api.get('/safras'),
        api.get('/talhoes'),
        api.get('/insumos')
      ]);

      setPlantios(Array.isArray(plantiosRes.data) ? plantiosRes.data : []);
      setSafras(Array.isArray(safrasRes.data) ? safrasRes.data : []);
      setTalhoes(Array.isArray(talhoesRes.data) ? talhoesRes.data : []);
      setInsumos(Array.isArray(insumosRes.data) ? insumosRes.data : []);
    } catch (error) {
      console.error('Erro ao carregar plantios', error);
      showError('Não foi possível carregar os plantios.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const salvar = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        safraId: Number(form.safraId),
        talhaoId: Number(form.talhaoId),
        sementeInsumoId: form.sementeInsumoId ? Number(form.sementeInsumoId) : null,
        cultura: form.cultura,
        variedade: form.variedade,
        dataPlantio: form.dataPlantio,
        areaPlantada: Number(form.areaPlantada || 0),
        quantidadeSementeUsada: Number(form.quantidadeSementeUsada || 0),
        unidade: form.unidade,
        custoEstimado: Number(form.custoEstimado || 0),
        status: Number(form.status),
        observacao: form.observacao || null
      };

      await api.post('/plantios', payload);
      setForm(initialForm);
      showSuccess('Plantio registrado e estoque de semente atualizado.');
      carregar();
    } catch (error) {
      console.error('Erro ao salvar plantio', error);
      showError('Não foi possível salvar o plantio.');
    }
  };

  const remover = async (id) => {
    const confirmou = await showConfirmation({
      title: 'Excluir plantio?',
      text: 'Essa acao remove o registro operacional do plantio.',
      confirmText: 'Excluir',
      cancelText: 'Cancelar'
    });
    if (!confirmou) return;

    try {
      await api.delete(`/plantios/${id}`);
      showSuccess('Plantio excluido.');
      carregar();
    } catch (error) {
      console.error('Erro ao excluir plantio', error);
      showError('Não foi possível excluir o plantio.');
    }
  };

  const columns = [
    { name: 'Data', selector: (row) => new Date(row.dataPlantio).toLocaleDateString(), sortable: true },
    { name: 'Safra', selector: (row) => row.safraDescricao || '-', sortable: true },
    { name: 'Talhão', selector: (row) => row.talhaoNome || '-', sortable: true },
    { name: 'Cultura', selector: (row) => row.cultura, sortable: true },
    { name: 'Variedade', selector: (row) => row.variedade, sortable: true },
    { name: 'Área ha', selector: (row) => row.areaPlantada, sortable: true, right: true },
    { name: 'Semente', selector: (row) => row.sementeNome || '-', sortable: true },
    { name: 'Status', selector: (row) => statusLabels[row.status] || row.status, sortable: true },
    {
      name: 'Ações',
      cell: (row) => (
        <button className="btn btn-outline-danger btn-sm" onClick={() => remover(row.id)}>
          <i className="bi bi-trash" />
        </button>
      ),
      ignoreRowClick: true
    }
  ];

  return (
    <div className="container mt-4">
      <Header />
          <div className="card shadow-lg border-0 rounded-3 mb-4">
            <div className="card-header bg-success text-white d-flex justify-content-between align-items-center rounded-top-3">
              <div className="d-flex align-items-center gap-3">
                <button
                  type="button"
                  className="btn btn-light btn-sm d-flex align-items-center gap-1"
                  onClick={() => navigate('/menu')}
                  disabled={loading}
                >
                  <i className="bi bi-arrow-left text-success" />
                  <span className="text-success fw-semibold">Voltar</span>
                </button>
                <div>
                  <h5 className="mb-0 fw-semibold">Plantio</h5>
                  <small className="text-white-50">Safra, talhão, área plantada e baixa de sementes</small>
                </div>
              </div>
              <button className="btn btn-light btn-sm text-success fw-semibold" onClick={carregar} disabled={loading}>
                <i className="bi bi-arrow-clockwise me-1" />
                Atualizar
              </button>
            </div>
            <form className="card-body" onSubmit={salvar}>
              <div className="row g-3">
                <div className="col-12 col-md-4">
                  <label className="form-label">Safra</label>
                  <select
                    className="form-select"
                    value={form.safraId}
                    onChange={(e) => {
                      const safra = safras.find((item) => String(item.id) === e.target.value);
                      setForm({
                        ...form,
                        safraId: e.target.value,
                        talhaoId: safra?.talhaoId || '',
                        cultura: safra?.cultura || form.cultura
                      });
                    }}
                    required
                  >
                    <option value="">Selecione</option>
                    {safras.map((item) => (
                      <option key={item.id} value={item.id}>{item.cultura} - {new Date(item.dataPlantio).getFullYear()}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">Talhão</label>
                  <select className="form-select" value={form.talhaoId} onChange={(e) => setForm({ ...form, talhaoId: e.target.value })} required>
                    <option value="">Selecione</option>
                    {talhoes.map((item) => (
                      <option key={item.id} value={item.id}>{item.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">Semente/Insumo</label>
                  <select className="form-select" value={form.sementeInsumoId} onChange={(e) => setForm({ ...form, sementeInsumoId: e.target.value })}>
                    <option value="">Sem baixa de estoque</option>
                    {insumos.map((item) => (
                      <option key={item.id} value={item.id}>{item.nome} - saldo {item.estoqueAtual}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">Cultura</label>
                  <input className="form-control" value={form.cultura} onChange={(e) => setForm({ ...form, cultura: e.target.value })} required />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">Variedade</label>
                  <input className="form-control" value={form.variedade} onChange={(e) => setForm({ ...form, variedade: e.target.value })} required />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label">Data</label>
                  <input type="date" className="form-control" value={form.dataPlantio} onChange={(e) => setForm({ ...form, dataPlantio: e.target.value })} required />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label">Área ha</label>
                  <input type="number" step="0.0001" className="form-control" value={form.areaPlantada} onChange={(e) => setForm({ ...form, areaPlantada: e.target.value })} required />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label">Semente</label>
                  <input type="number" step="0.0001" className="form-control" value={form.quantidadeSementeUsada} onChange={(e) => setForm({ ...form, quantidadeSementeUsada: e.target.value })} />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label">Unidade</label>
                  <input className="form-control" value={form.unidade} onChange={(e) => setForm({ ...form, unidade: e.target.value })} required />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label">Custo</label>
                  <CurrencyInput className="form-control" value={form.custoEstimado} onValueChange={(value) => setForm({ ...form, custoEstimado: value })} placeholder="0,00" />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-4 d-flex align-items-end justify-content-between gap-3">
                  <span className="text-muted small">
                    {talhaoDaSafra ? `Área do talhão: ${talhaoDaSafra.areaHa} ha` : 'Selecione uma safra para travar o talhão.'}
                  </span>
                  <button className="btn btn-success" type="submit" disabled={loading}>
                    <i className="bi bi-save me-1" />
                    Registrar plantio
                  </button>
                </div>
              </div>
            </form>
          </div>

          <div className="card shadow-lg border-0 rounded-3">
            <div className="card-header bg-success text-white">
              <h6 className="mb-0 fw-semibold">Plantios registrados</h6>
            </div>
            <div className="card-body">
              <DataTable
                columns={columns}
                data={plantios}
                pagination
                progressPending={loading}
                highlightOnHover
                striped
                responsive
                noDataComponent="Nenhum plantio registrado."
              />
            </div>
          </div>
    </div>
  );
}

export default PlantioList;
